import { useState, useCallback, useMemo, useEffect } from 'react';
import { Col, Row } from 'react-bootstrap';

import { Select } from 'shared/form/components/Select';
import { useNotifications } from 'shared/notifications';
import { useCoreService, useReporteService } from 'core/services/useCoreService';
import { SelectOption } from 'shared/form/models/SelectOption';
import { FormatSelector } from '../FormatSelector/FormatSelector';
import { Tarjeta } from '../../../tarjeta/models/Tarjeta';
import { useFileDownloader } from 'shared/hooks/useFileDownloader';
import { generateReportFileName } from '../../util/fileNameGenerator';
import { ReportFormActions } from '../ReportFormActions/ReportFormActions';
import { ReportFormat } from '../../models/ReportModels'
import { Periodo } from 'shared/models/Periodo';
import { generatePeriods } from 'shared/form/data/generatePeriods';
import { CentroCosto } from "core/centrocosto/models/CentroCosto";

interface FormData {
  idCentroCosto: number | null;
  idTarjeta: number | null;
  formato: SelectOption<ReportFormat> | null;
  periodo: Periodo | null;
}

export const MovimientosPorTarjetaForm = () => {
  const { generateMovimientosPorTarjetaReport } = useReporteService();
  const { addNotification } = useNotifications();
  const coreService = useCoreService();
  const { downloadFile } = useFileDownloader();
  const [cardOptions, setCardOptions] = useState<SelectOption<number>[]>([]);
  const [allCards, setAllCards] = useState<Tarjeta[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingCards, setIsLoadingCards] = useState(true);

  const [centroCostosOptions, setCentroCostosOptions] = useState<SelectOption<number>[]>([]);
  const [allCentroCostos, setAllCentroCostos] = useState<CentroCosto[]>([]);
  const [isLoadingCentroCostos, setIsLoadingCentroCostos] = useState(true);

  const periodOptions = useMemo(
    () =>
      generatePeriods().filter(
        (option) => option.label !== 'Periodo Actual'
      ),
    [],
  );

  const [formData, setFormData] = useState<FormData>({
    idCentroCosto: null,
    idTarjeta: null,
    formato: null,
    periodo: null,
  });

  useEffect(() => {
    const fetchCentroCostos = async () => {
      setIsLoadingCentroCostos(true);

      try {
        const response = await coreService.getCostCentres();
        setAllCentroCostos(response.content);
        setCentroCostosOptions(
          response.content.map((cc: CentroCosto) => ({
            value: cc.id,
            label: cc.nombre,
          })),
        );
      } catch {
        addNotification(
          'No se pudieron cargar los Centros de costo.',
          'error',
        );
      } finally {
        setIsLoadingCentroCostos(false);
      }
    };

    fetchCentroCostos();
  }, [coreService, addNotification]);

  useEffect(() => {
    if (!formData.idCentroCosto) {
      setCardOptions([]);
      setAllCards([]);
      return;
    }

    const fetchCards = async () => {
      setIsLoadingCards(true);

      try {
        const response = await coreService.getAllTarjetas({
          centroCostoOrigenId: formData.idCentroCosto,
        });

        setAllCards(response.content);

        setCardOptions([
          {
            value: null,
            label: 'Todas las tarjetas',
          },
          ...response.content.map((card: Tarjeta) => ({
            value: card.id,
            label: `**** ${card.numero.slice(-4)} - ${card.denominacion}`,
          })),
        ]);
      } catch {
        addNotification(
          'No se pudieron cargar las tarjetas.',
          'error',
        );

        setCardOptions([]);
        setAllCards([]);
      } finally {
        setIsLoadingCards(false);
      }
    };

    fetchCards();
  }, [formData.idCentroCosto, coreService, addNotification]);
  const handleInputChange = useCallback((name: keyof FormData, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  }, []);

  const handleGenerateReport = async (event: React.FormEvent) => {
    event.preventDefault();
    setIsLoading(true);

    if (
      !formData.idCentroCosto ||
      !formData.formato ||
      !formData.periodo
    ) {
      addNotification(
        'Debe seleccionar un Centro de costo, un período y un formato.',
        'warning',
      );
      setIsLoading(false);
      return;
    }

    try {
      const { periodo } = formData;

      const fileData = await generateMovimientosPorTarjetaReport({
        idCentroCosto: formData.idCentroCosto,
        idTarjeta: formData.idTarjeta,
        formato: formData.formato.value,
        mes: periodo.mes,
        anio: periodo.anio,
      });

      // --- 2. Construimos el nombre del archivo ---
      const selectedCard =
        allCards.find(card => card.id === formData.idTarjeta);

      const selectedCentroCosto =
        allCentroCostos.find(cc => cc.id === formData.idCentroCosto);

      const fileName = generateReportFileName({
        reporteBase: 'ReporteMovimientosPorTarjeta',
        identificador: selectedCentroCosto?.numeroCuenta?.toString() ?? 'N-A',
        denominacion:
          selectedCard?.denominacion ??
          selectedCentroCosto?.nombre ??
          'SinDenominacion',
        fecha: new Date(
          periodo.anio,
          periodo.mes - 1,
          1,
        ),
        extension: formData.formato.value,
      });

      // --- 3. Llamamos a la función de descarga del hook ---
      downloadFile(fileData, fileName);

      addNotification(
        'Reporte generado exitosamente. La descarga ha comenzado.',
        'success',
      );
    } catch (error) {
      console.error('Error al generar el reporte:', error);
      addNotification('Hubo un error al generar el reporte.', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClearForm = () => {
    setFormData({
      idCentroCosto: null,
      idTarjeta: null,
      formato: null,
      periodo: null,
    });
  };

  return (
    <form onSubmit={handleGenerateReport}>
      <Col className="mb-4">
        <Select
          label="Centro de Costo"
          name="idCentroCosto"
          options={centroCostosOptions}
          value={
            centroCostosOptions.find(
              option => option.value === formData.idCentroCosto
            ) || null
          }
          onChange={(option: SelectOption<number>) => {
            handleInputChange('idCentroCosto', option?.value ?? null);

            // limpia la tarjeta al cambiar el centro
            handleInputChange('idTarjeta', null);
          }}
          placeholder={
            isLoadingCentroCostos
              ? 'Cargando Centros de costo...'
              : 'Seleccione un Centro de costo'
          }
          isDisabled={isLoadingCentroCostos}
        />
      </Col>

      <Col className="mb-4">
        <Select
          label="Tarjeta"
          name="idTarjeta"
          options={cardOptions}
          value={
            cardOptions.find(
              option => option.value === formData.idTarjeta
            ) || null
          }
          onChange={(option: SelectOption<number | null>) => {
            handleInputChange('idTarjeta', option?.value ?? null);
          }}
          placeholder={
            !formData.idCentroCosto
              ? 'Seleccione primero un Centro de costo'
              : isLoadingCards
                ? 'Cargando tarjetas...'
                : 'Todas las tarjetas'
          }
          isDisabled={
            !formData.idCentroCosto || isLoadingCards
          }
        />
      </Col>

      <Col className="mb-4">
        <FormatSelector
          value={formData.formato}
          onChange={(option) => handleInputChange('formato', option)}
        />
      </Col>

      <Row className="align-items-end">
        <Col md={6}>
          <Select
            label="Periodo"
            name="periodo"
            options={periodOptions}
            value={
              periodOptions.find(
                (option) => option.value?.equals(formData.periodo)
              ) || null
            }
            onChange={(option: SelectOption<Periodo>) => {
              handleInputChange('periodo', option?.value ?? null);
            }}
            placeholder="Seleccione un período"
          />
        </Col>

        <Col md={6}>
          <ReportFormActions
            onClear={handleClearForm}
            isLoading={isLoading}
          />
        </Col>
      </Row>
    </form>
  );
};
